import { hasScope } from '@nyce/admin-kit/api';
import { SCOPES } from '../../api';
import { useAsync } from '../../components/common';
import { useHubLogin } from '../../session';
import type { Issue } from '../../pages/dashboard/shared';
import { billing, type BillingCatalog, type BillingRun, type BillingSchedule } from '../api/client';

// Billing at a glance, for the Billing group dashboard and the main dashboard: the catalog (which reports can run where),
// the schedules, and the newest runs.

export interface BillingSummary {
  catalog: BillingCatalog;
  schedules: BillingSchedule[];
  runs: BillingRun[];
}

export const RECENT = 50;

export function useBillingSummary() {
  const enabled = hasScope(useHubLogin()?.scopes, SCOPES.billingRead);
  const state = useAsync(async (): Promise<BillingSummary | null> => {
    if (!enabled) return null;
    const [catalog, schedules, runs] = await Promise.all([
      billing.catalog(),
      billing.schedules(),
      billing.runs({ limit: RECENT }),
    ]);
    return { catalog, schedules, runs };
  }, [enabled]);
  return { ...state, enabled };
}

/** The next enabled schedule to fire. */
export const nextSchedule = (s: BillingSummary) =>
  s.schedules
    .filter((x) => x.enabled && x.nextRunAt)
    .sort((a, b) => (a.retryAt ?? a.nextRunAt!).localeCompare(b.retryAt ?? b.nextRunAt!))[0];

/** What needs a look: no mail relay, schedules retrying a failed slot, schedules on an unconfigured environment, the latest scheduled run per schedule failing. */
export function billingIssues(s: BillingSummary): Issue[] {
  const issues: Issue[] = [];
  const enabled = s.schedules.filter((x) => x.enabled);
  if (enabled.length && !s.catalog.mailConfigured)
    issues.push({
      key: 'mail',
      status: 'error',
      name: 'Mail',
      what: 'billing',
      detail: 'no mail relay configured: schedules cannot send',
      to: '/billing/schedules',
    });
  for (const x of enabled) {
    const report = s.catalog.reports.find((r) => r.id === x.reportId);
    const target = report?.targets.find((t) => t.id === x.target);
    if (!target?.configured)
      issues.push({
        key: `cfg/${x.id}`,
        status: 'warn',
        name: x.name,
        what: 'billing schedule',
        detail: `${report?.product ?? x.reportId} has no hub credential in ${target?.label ?? x.target}`,
        to: '/billing/schedules',
      });
    if (x.retryAt)
      issues.push({
        key: `retry/${x.id}`,
        status: 'warn',
        name: x.name,
        what: 'billing schedule',
        detail: `failed ${x.attempts}×, retrying`,
        to: x.lastRunId ? `/billing/reports?run=${x.lastRunId}` : '/billing/schedules',
      });
  }
  const latest = new Map<string, BillingRun>();
  for (const r of s.runs) if (r.scheduleId && !latest.has(r.scheduleId)) latest.set(r.scheduleId, r);
  for (const [id, r] of latest)
    if ((r.status === 'failed' || r.sendError) && !s.schedules.find((x) => x.id === id)?.retryAt)
      issues.push({
        key: `run/${r.id}`,
        status: 'error',
        name: s.schedules.find((x) => x.id === id)?.name ?? id,
        what: 'billing run',
        detail: `${r.periodLabel}: ${r.error ?? r.sendError}`,
        to: `/billing/reports?run=${r.id}`,
      });
  return issues;
}
